import Vue from 'vue';
import isEmpty from 'lodash/isEmpty';
import keyBy from 'lodash/keyBy';
import values from 'lodash/values';
import {
    createWebhook,
    deleteWebhook,
    fetchWebhooks,
    updateWebhook,
    pingWebhook,
    fetchWebhookErrorLogs,
} from './webhooks.utils';

const webhooks = {
    namespaced: true,
    state: {
        error: {},
        map: {},
        // Some of these initial values are temporary until we have more types,
        // event types, and schema versions to choose from
        webhook: {
            active: false,
            eventCategories: ['allEvents'],
            name: 'New Webhook',
            url: '',
            secret: '',
            type: 'segment',
        },
        webhooksErrorLogs: {},
    },
    mutations: {
        addWebhookToMap (state, { webhook }) {
            Vue.set(state.map, webhook.id, webhook);
        },
        deleteWebhookFromMap (state, { id }) {
            Vue.delete(state.map, id);
        },
        setActiveWebhook (state, { webhook }) {
            state.webhook = webhook;
        },
        setError (state, { error }) {
            state.error = error;
        },
        setMap (state, { map }) {
            state.map = map;
        },
        setMapAtKey (state, { id, webhook }) {
            Vue.set(state.map, id, webhook);
        },
        setWebhookStatus (state, { id, active }) {
            Vue.set(state.map[id], 'active', active);
        },
        setWebhookUnlocked (state, { id }) {
            Vue.set(state.map[id], 'isLocked', false);
        },
        setWebhooksErrorLogs (state, { logs }) {
            state.webhooksErrorLogs = logs;
        },
    },
    actions: {
        async createWebhook ({ commit }, webhookToCreate) {
            try {
                const webhook = await createWebhook({ webhook: webhookToCreate });
                commit('addWebhookToMap', { webhook: webhook.data });
            } catch (error) {
                commit('setError', { error });
                throw error;
            }
        },
        async deleteWebhook ({ commit }, { webhookId }) {
            try {
                await deleteWebhook({ id: webhookId });
                commit('deleteWebhookFromMap', { id: webhookId });
            } catch (error) {
                commit('setError', { error });
                throw error;
            }
        },
        async editWebhook ({ commit }, webhook) {
            try {
                await updateWebhook({ webhook });
                commit('setMapAtKey', { id: webhook.id, webhook });
            } catch (error) {
                commit('setError', { error });
                throw error;
            }
        },
        async loadAll ({ commit, state }, { reFetch = false } = {}) {
            if (!isEmpty(state.map) && !reFetch) {
                return;
            }
            try {
                const webhooksList = await fetchWebhooks();
                const lockedList = keyBy(
                    webhooksList.data.filter(({ isLocked }) => isLocked),
                    'id',
                );
                const unlockedList = keyBy(
                    webhooksList.data.filter(({ isLocked }) => !isLocked),
                    'id',
                );
                commit('setMap', { map: { ...lockedList, ...unlockedList } });
            } catch (error) {
                commit('setError', { error });
                throw error;
            }
        },
        async loadWebhooksErrorLogs ({ commit, state }) {
            try {
                let webhooksErrorLogs = {};
                const promises = [];
                const lockedWebhookIDs = [];
                for (const webhookId in state.map) {
                    if (state.map[webhookId].isLocked) {
                        lockedWebhookIDs.push(webhookId);
                        promises.push(fetchWebhookErrorLogs({ id: webhookId }));
                    }
                }
                const responseObj = await Promise.all(promises);
                webhooksErrorLogs = responseObj.reduce((obj, log, index) => {
                    obj[lockedWebhookIDs[index]] = log.data;

                    return obj;
                }, {});
                commit('setWebhooksErrorLogs', { logs: webhooksErrorLogs });
            } catch (error) {
                commit('setError', { error });
                throw error;
            }
        },
        pingWebhook (___, { url, secret }) {
            return pingWebhook({ url, secret });
        },
        async updateWebhookStatus ({ commit }, { webhook, active }) {
            try {
                commit('setWebhookStatus', { id: webhook.id, active });

                if (webhook.isLocked) {
                    await this.unlockWebhook({ commit }, webhook);

                    return;
                }
                await updateWebhook({ webhook });
            } catch (error) {
                commit('setError', { error });
                throw error;
            }
        },
        async unlockWebhook ({ commit }, webhook) {
            try {
                commit('setWebhookUnlocked', { id: webhook.id });
                await updateWebhook({ webhook });
            } catch (error) {
                commit('setError', { error });
                throw error;
            }
        },
    },
    getters: {
        list: (state) => values(state.map),
        getWebhookById: (state) => (id) => state.map[id],
        logs: (state) => state.webhooksErrorLogs,
    },
};

export default webhooks;
